import React, { memo, useCallback, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
} from 'react-native';
import type { ThreadSortBy } from '@/types/residents/corkBoard';
import { CORK_BOARD_SORT_OPTIONS } from '@/constants/residents/corkBoardConstants';
import { SEMANTIC_COLORS, COMPONENT_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface SortDropdownProps {
  value: ThreadSortBy;
  onChange: (sortBy: ThreadSortBy) => void;
}

export const SortDropdown: React.FC<SortDropdownProps> = memo(({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentLabel = CORK_BOARD_SORT_OPTIONS.find((opt) => opt.value === value)?.label ?? 'Sort';

  const handleSelect = useCallback((sortBy: ThreadSortBy) => {
    onChange(sortBy);
    setIsOpen(false);
  }, [onChange]);

  return (
    <View style={styles.wrapper}>
      <TouchableOpacity
        style={styles.trigger}
        onPress={() => setIsOpen(true)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`Sort by ${currentLabel}`}
        accessibilityState={{ expanded: isOpen }}
        accessibilityHint="Opens sort options"
      >
        <Text style={styles.label}>Sort:</Text>
        <Text style={styles.value}>{currentLabel}</Text>
        <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN width={14} height={14} color={SEMANTIC_COLORS.text.muted} />
      </TouchableOpacity>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <Pressable style={styles.overlay} onPress={() => setIsOpen(false)}>
          <View style={styles.dropdown}>
            <Text style={styles.dropdownTitle}>Sort by</Text>
            {CORK_BOARD_SORT_OPTIONS.map((option) => {
              const isSelected = option.value === value;
              return (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.option, isSelected && styles.optionSelected]}
                  onPress={() => handleSelect(option.value)}
                  activeOpacity={0.7}
                  accessibilityRole="radio"
                  accessibilityLabel={option.label}
                  accessibilityState={{ selected: isSelected }}
                >
                  <Text style={[styles.optionText, isSelected && styles.optionTextSelected]}>
                    {option.label}
                  </Text>
                  {isSelected && <RESIDENTS_SVG_COMPONENTS.CHECK width={16} height={16} color={SEMANTIC_COLORS.interactive.primary} />}
                </TouchableOpacity>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
});

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    height: 40,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },
  label: {
    fontSize: 13,
    color: SEMANTIC_COLORS.text.muted,
  },
  value: {
    fontSize: 13,
    fontWeight: '500',
    color: SEMANTIC_COLORS.text.primary,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  dropdown: {
    backgroundColor: COMPONENT_COLORS.card.background,
    borderRadius: 14,
    width: '100%',
    maxWidth: 300,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: COMPONENT_COLORS.card.border,
  },
  dropdownTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: SEMANTIC_COLORS.border.tertiary,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionSelected: {
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },
  optionText: {
    fontSize: 15,
    color: SEMANTIC_COLORS.text.primary,
  },
  optionTextSelected: {
    color: SEMANTIC_COLORS.interactive.primary,
    fontWeight: '600',
  },
});
